import { useMemo } from "react"
import {
  periodFrom,
  type Period,
} from "@/v2-backoffice-client/signIn/signIn.period"
import type { SignInFilters } from "@/v2-backoffice-client/signIn/signIn.filters"
import { useStoredActivitiesQuery } from "@/v2-backoffice-client/signIn/storedActivities.query"

const PAGE_SIZE = 200

// useSignInActivities is the Events tab's single data concern: a paged page of
// sign-in events for the selected period. Every period reads from the durable
// store (acc.UserSignInEvents, kept current by v2-auth-monitor every ~5 min and
// already geoip-enriched at ingest) — so all periods behave identically: fast,
// server-filtered, infinite-loaded. Real-time data lives on the per-user panel
// (a direct MS Graph call), not here. `from` is the only window knob; "Last
// 100"/recency maps to no `from` (newest-first, paged).
export function useSignInActivities(period: Period, filters: SignInFilters) {
  // periodFrom() resolves `new Date()`, so it MUST be memoised on `period` —
  // an unmemoised value changes every render and drives Apollo into an infinite
  // refetch loop (the Compiler can't memoise a non-deterministic value).
  const from = useMemo(() => periodFrom(period), [period])

  // The cache merge (signin.cache.ts) appends pages keyed on the filter set, so
  // changing a filter or window starts a fresh list; fetchMore bumps the offset.
  const storedInput = { from, limit: PAGE_SIZE, offset: 0, ...filters }
  const stored = useStoredActivitiesQuery({
    variables: { input: storedInput },
    notifyOnNetworkStatusChange: true,
  })

  const items = stored.data?.getStoredSignInActivities.items ?? []
  return {
    activities: items,
    loading: stored.loading,
    error: stored.error,
    refetch: stored.refetch,
    pageInfo: stored.data?.getStoredSignInActivities.pageInfo ?? null,
    fetchMore: () =>
      stored.fetchMore({
        variables: { input: { ...storedInput, offset: items.length } },
      }),
  }
}
